import FilesApi from "../../../Api/Services/AppflyteBackend/FileServiceApi";
import TemplateExtractionApi from "../../../Api/Services/AppflyteBackend/TemplateExtractionApi";
import { lowercaseStrings } from "../../../utils";
import { apiErrorHandler } from "../../../utils/ApiErrorHandler";
import { extractionFileSave } from "../../../utils/ApiFunctions/ExtractionData";
import { getDoctypeSchema } from "../../../utils/ApiFunctions/ExtractionSchemas";
import handleFileUpload from "../../../utils/fileUpload";
import { getSessionData } from "../../../utils/sessionDataHandle";

// Constants
const POLL_INTERVAL = 3000;
const INITIAL_DELAY = 3000;
const DEFAULT_FILE_NAME = "downloaded-file";
const DEFAULT_MIME_TYPE = "application/octet-stream";

const ENTITY_FILTERS = ["DATE", "TIME", "ADDRESS", "EMAIL", "PHONE"];

class ExtractionError extends Error {
    constructor(message, code) {
        super(message);
        this.name = "ExtractionError";
        this.code = code;
    }
}

export const handleExecuteExtraction = async (document_type_id, document_type_name, platform_file_id,
    pollingIntervalRef, onProgress = null, navigate) => {
    try {
        onProgress?.({ status: 'fetching', message: 'Fetching file...', progress: 20 });
        const { file, file_url } = await fetchAndPrepareFile(platform_file_id);
        const fileName = file.name;

        onProgress?.({ status: 'uploading', message: 'Uploading file...', progress: 40 });
        const { fileId, fileUrl } = await uploadAndSaveFile(file, document_type_id);

        onProgress?.({ status: 'extracting', message: 'Fetching schema...', progress: 60 });
        const schema_data = await fetchSchema(document_type_id);

        if (!schema_data) {
            throw new ExtractionError("Schema data not found", "SCHEMA_NOT_FOUND");
        }

        onProgress?.({ status: 'extracting', message: 'Extracting data...', progress: 70 });
        const pipeline_exec_id = await handleExtraction(document_type_id, document_type_name, schema_data, fileId,
            fileName, file_url, pollingIntervalRef, onProgress, navigate);

        return { fileId, fileUrl, fileName, pipeline_exec_id };
    } catch (error) {
        console.error("Extraction execution failed:", error);
        apiErrorHandler(error);
        throw error;
    }
};


const fetchAndPrepareFile = async (platform_file_id) => {
    const { file_name, file_url } = await fetchPlatformFile(platform_file_id);

    if (!file_url) {
        throw new ExtractionError("File URL not found", "FILE_URL_MISSING");
    }

    const response = await fetch(file_url);
    if (!response.ok) {
        throw new ExtractionError(
            `Failed to fetch file: ${response.statusText}`,
            "FILE_FETCH_FAILED"
        );
    }

    const blob = await response.blob();
    const file = new File([blob], file_name || DEFAULT_FILE_NAME, {
        type: blob.type || DEFAULT_MIME_TYPE,
    });

    return { file, file_url };
};


const uploadAndSaveFile = async (file, document_type_id) => {
    const file_name = file.name || DEFAULT_FILE_NAME;
    const upload_response = await handleFileUpload(file_name, file);

    if (upload_response.status !== 200) {
        throw new ExtractionError("File upload failed", "UPLOAD_FAILED");
    }

    const { fileName, fileUrl, fileId } = upload_response;
    const save_response = await extractionFileSave(fileName, fileUrl, fileId, document_type_id);

    if (save_response.status !== 200) {
        throw new ExtractionError("Backend save failed", "SAVE_FAILED");
    }

    return { fileId, fileUrl }
};

const fetchPlatformFile = async (file_id) => {
    try {
        const platform_file_response = await TemplateExtractionApi.getExtractionPlatformFile(file_id);

        if (platform_file_response.status !== 200) {
            return { file_name: null, file_url: null };
        }

        const payload = platform_file_response?.data?.payload ?? {};
        const { bucket_name, object_path } = payload?.advanced ?? {};
        const file_name = payload?.file_name ?? null;

        if (!bucket_name || !object_path) {
            return { file_name, file_url: null };
        }

        const file_url = await fetchDownloadUrl(bucket_name, object_path);
        return { file_name, file_url };
    } catch (error) {
        console.error("Error fetching platform file:", error);
        return { file_name: null, file_url: null };
    }
};

const fetchDownloadUrl = async (bucket_name, object_path) => {
    try {
        const fileResponse = await FilesApi.getDownloadUrl(bucket_name, object_path);

        if (fileResponse.status !== 200) {
            return null;
        }

        const latest = fileResponse?.data?.at(-1);
        return latest ? Object.values(latest).at(-1) ?? null : null;
    } catch (error) {
        console.error("Error fetching download URL:", error);
        return null;
    }
};


const fetchSchema = async (document_type_id) => {
    try {
        const response = await getDoctypeSchema(document_type_id);

        if (!response?.length) {
            return null;
        }

        const responseData = response.at(-1) ?? {};
        return responseData?.payload?.settings ?? null;
    } catch (error) {
        console.error("Error fetching schema:", error);
        return null;
    }
};


const handleExtraction = async (document_type_id, document_type_name, schema_data,
    file_id, file_name, file_url, pollingIntervalRef) => {
    try {
        // Execute data extraction
        const { success, pipeline_exec_id } = await handleDataExtraction(document_type_id, document_type_name, schema_data,
            file_id, file_name, file_url, pollingIntervalRef);

        if (!success) {
            throw new ExtractionError("Data extraction failed", "EXTRACTION_FAILED");
        }

        // Fetch layout with lowercased schema
        const updatedSchemaData = await lowercaseStrings(schema_data);
        const entityResponse = await TemplateExtractionApi.fetchLayout(JSON.stringify(updatedSchemaData), file_name);

        if (entityResponse.status !== 200) {
            throw new ExtractionError("Layout fetch failed", "LAYOUT_FETCH_FAILED");
        }

        return pipeline_exec_id;

    } catch (error) {
        console.error("Error in handleExtraction:", error);
        apiErrorHandler(error);
        throw error;
    }
};

/**
 * Handles data extraction with pipeline polling
 */
const handleDataExtraction = async (document_type_id, document_type_name, schema_data, file_id,
    file_name, file_url, pollingIntervalRef) => {
    try {
        const selected_project = getSessionData("selected_project");
        const project_id = selected_project?.payload?.__auto_id__;

        const reqObj = buildExtractionRequest({ file_url, file_id, file_name, schema_data, document_type_name, document_type_id, project_id });
        const pipeline_exec_id = await startExtraction(reqObj);
        await sleep(INITIAL_DELAY);
        const success = await pollPipelineStatus(pipeline_exec_id, pollingIntervalRef);
        return { success, pipeline_exec_id };

    } catch (error) {
        console.error("Error in handleDataExtraction:", error);
        apiErrorHandler(error);
        cleanupPolling(pollingIntervalRef);
        throw error;
    }
};


const buildExtractionRequest = ({ file_url, file_id, file_name, schema_data, document_type_name, document_type_id, project_id, }) => ({
    inputs: {
        pdf_parser_block: { file_path: file_url },
        entity_extraction_block_v2: {
            filter: ENTITY_FILTERS,
            file_id,
            pdf_filename: file_name,
            schema: schema_data,
            document_type: document_type_name,
            document_type_id,
            project_id,
            analytics_dataset_status: true,
            upload_via: "API",
        },
    },
});


const startExtraction = async (reqObj) => {
    const extractionResponse = await TemplateExtractionApi.startExtraction(JSON.stringify(reqObj));

    if (extractionResponse?.status !== 200) {
        throw new ExtractionError("Extraction initiation failed", "INITIATION_FAILED");
    }

    const pipeline_exec_id = extractionResponse.data?.pipeline_exec_id;
    if (!pipeline_exec_id) {
        throw new ExtractionError("Missing pipeline execution ID", "MISSING_EXEC_ID");
    }

    return pipeline_exec_id;
};


const pollPipelineStatus = (pipeline_exec_id, pollingIntervalRef, onProgress = null) => {
    return new Promise((resolve) => {
        cleanupPolling(pollingIntervalRef);

        let pollCount = 0;
        const maxPolls = 60;

        pollingIntervalRef.current = setInterval(async () => {
            try {
                pollCount++;
                const progress = Math.min(70 + (pollCount / maxPolls) * 20, 90);
                onProgress?.({ status: 'extracting', message: `Processing... (${pollCount * 3}s)`, progress });
                const statusRes = await TemplateExtractionApi.getPipelineStatus(pipeline_exec_id);

                if (statusRes === null) {
                    cleanupPolling(pollingIntervalRef);
                    resolve(false);
                    return;
                }

                if (statusRes?.status !== 200) {
                    return;
                }

                const pipeline_status = statusRes.data?.status;

                if (pipeline_status === "completed") {
                    cleanupPolling(pollingIntervalRef);
                    onProgress?.({ status: 'processing', message: 'Finalizing...', progress: 95 });
                    resolve(true);
                } else if (pipeline_status === "failed") {
                    cleanupPolling(pollingIntervalRef);
                    resolve(false);
                }

                if (pollCount >= maxPolls) {
                    cleanupPolling(pollingIntervalRef);
                    resolve(false);
                }
            } catch (error) {
                cleanupPolling(pollingIntervalRef);
                console.error("Error during pipeline polling:", error);
                apiErrorHandler(error);
                resolve(false);
            }
        }, POLL_INTERVAL);
    });
};

const cleanupPolling = (pollingIntervalRef) => {
    if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
    }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));